import { faAngleLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useFetchProductsByReleaseIdQuery } from "../../../api/products/hooks/usefetchProductsByReleaseIdQuery";
import { useFetchReleaseQuery } from "../../../api/releases/hooks/useFetchReleaseQuery";
import { useFetchReleaseTracksQuery } from "../../../api/releases/hooks/useFetchReleaseTracksQuery";
import { useProductCart } from "../../../atoms/products/hooks/useProductCart";
import { productPriceDetailsAtom } from "../../../atoms/products/productPriceDetails";
import { selectedFeaturedBundleIndexAtom } from "../../../atoms/profileScreen/artistReleaseTabView";
import { Button } from "../../core-ui/components/Button/Button";
import { ButtonVariant } from "../../core-ui/components/Button/utils";
import { Text } from "../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../core-ui/components/Text/TextUtils";
import { ArtistReleaseTrackTable } from "../ArtistReleaseTrackTable/ArtistReleaseTrackTable";
import { FeaturedArtistRelease } from "../FeaturedArtistRelease/FeaturedArtistRelease";
import { HardCodedReleaseCredits } from "./HardCodedReleaseCredits";
import { SoundWaveLoader } from "../../elements/SoundWaveLoader/SoundWaveLoader";

interface ReleaseDetailViewProps {
  releaseId: number;
  navigateBack?: () => void;
  hidePurchaseButton?: boolean;
  backButtonText?: string;
}

export const ReleaseDetailView = ({
  releaseId,
  navigateBack,
  backButtonText = "Back",
  hidePurchaseButton = true,
}: ReleaseDetailViewProps) => {
  const { cart } = useProductCart();
  const [, setPrice] = useAtom(productPriceDetailsAtom);
  const { data: release, isPending } = useFetchReleaseQuery(releaseId);
  const { data: releaseTracks, isPending: isLoadingReleaseTracks } =
    useFetchReleaseTracksQuery(releaseId);
  const { data: products } = useFetchProductsByReleaseIdQuery(releaseId);
  const [selectedProductIndex, setSelectedProductIndex] = useAtom(
    selectedFeaturedBundleIndexAtom,
  );
  const hasSetInitialSelectedProduct = useRef(false);
  const isCartEmpty = cart.length === 0;
  const hasSelectedProduct = selectedProductIndex !== "";

  const availableProducts = useMemo(() => {
    if (!products) return [];
    const now = new Date();
    return products.filter((product) => {
      // Filter out products that have not been published
      if (
        !product.published_at ||
        (product.published_at && new Date(product.published_at) > now)
      ) {
        return false;
      }

      // Filter out products that have ended
      if (product.published_end && new Date(product.published_end) < now) {
        return false;
      }
      return true;
    });
  }, [products]);

  const selectedProduct = useMemo(() => {
    if (selectedProductIndex === "" || !products) return undefined;

    return products.find((product) => product.id === selectedProductIndex);
  }, [products, selectedProductIndex]);
  const releaseIsStreamEnabled = useMemo(
    () => Boolean(release?.supply_chain_product_id),
    [release],
  );

  // After fetching the list of products, if a product is already in the cart,
  // Then it should be the selected product
  useEffect(() => {
    if (
      products &&
      products.length > 0 &&
      !isCartEmpty &&
      !hasSelectedProduct &&
      !hasSetInitialSelectedProduct.current
    ) {
      if (!releaseTracks) return;
      for (const cartItem of cart) {
        if (products?.find((product) => product.id === cartItem.product_id)) {
          if (releaseTracks.length === cartItem.release_track_ids.length) {
            setSelectedProductIndex(cartItem.product_id);
            break;
          }
        }
      }
      hasSetInitialSelectedProduct.current = true;
    }
  }, [
    cart,
    hasSelectedProduct,
    isCartEmpty,
    products,
    releaseTracks?.length,
    selectedProductIndex,
    setSelectedProductIndex,
    releaseTracks,
  ]);

  useEffect(() => {
    return () => {
      setSelectedProductIndex("");
    };
  }, [setSelectedProductIndex]);

  const lowestPricedProduct = useMemo(() => {
    if (!products || products.length === 0) return undefined;

    return products.reduce((currentLowestPricedProduct, currentProduct) => {
      return parseFloat(currentProduct.unit_price) <
        parseFloat(currentLowestPricedProduct.unit_price)
        ? currentProduct
        : currentLowestPricedProduct;
    }, products[0]);
  }, [products]);

  const productToGetPriceFrom = selectedProduct || lowestPricedProduct;

  // If all individual tracks are selected and no bundle was selected,
  // we'll auto-select the bundle with the lowest unit price
  useEffect(() => {
    if (
      lowestPricedProduct &&
      releaseTracks?.length ===
        cart.find((cartItem) => cartItem.product_id === lowestPricedProduct.id)
          ?.release_track_ids.length &&
      !hasSelectedProduct
    ) {
      setSelectedProductIndex(lowestPricedProduct.id);
    }
  }, [
    cart,
    hasSelectedProduct,
    lowestPricedProduct,
    releaseTracks?.length,
    setSelectedProductIndex,
  ]);

  const hasUnitPrice = Boolean(productToGetPriceFrom?.unit_price);
  const unitPriceDiffersFromTotalPrice =
    productToGetPriceFrom?.unit_price !== productToGetPriceFrom?.total_price;
  const isPayWhatYouWant = productToGetPriceFrom?.pay_what_you_want;

  const unitPrice =
    hasUnitPrice && unitPriceDiffersFromTotalPrice && !isPayWhatYouWant
      ? productToGetPriceFrom?.unit_price
      : undefined;

  const currency = productToGetPriceFrom?.currency;

  useEffect(() => {
    if (!products) return;
    products.forEach((currentProduct) => {
      setPrice({
        [currentProduct.id]: {
          unitPrice: currentProduct.unit_price
            ? Number(currentProduct.unit_price)
            : 0,
          totalPrice: currentProduct.total_price
            ? Number(currentProduct.total_price)
            : 0,
        },
      });
    });
  }, [products, setPrice]);

  return (
    <Box p={2} sx={{ display: "flex", flexDirection: "column", gap: "54px" }}>
      {release && !isPending && (
        <FeaturedArtistRelease
          release={release}
          products={availableProducts}
          selectedProductIndex={
            hidePurchaseButton ? undefined : selectedProductIndex
          }
          setSelectedProductIndex={setSelectedProductIndex}
          releaseTracks={releaseTracks}
          isOnReleaseDetailsView={true}
        />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {selectedProduct && !hidePurchaseButton && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/*{Boolean(selectedProduct?.purchase_limit) && (*/}
            {/*  <Text>*/}
            {/*    <strong>Limited quantity: </strong>*/}
            {/*    {`${selectedProduct?.available_quantity} / ${selectedProduct?.purchase_limit} remaining`}*/}
            {/*  </Text>*/}
            {/*)}*/}
            <Text bold variant={TextStyleVariant.S2}>
              Bundle Description
            </Text>
            <Text
              style={{
                whiteSpace: "pre-wrap",
              }}
            >
              {selectedProduct.description}
            </Text>
          </Box>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text bold variant={TextStyleVariant.S2}>
              Release Tracks
            </Text>
            {navigateBack && (
              <Button onClick={navigateBack} variant={ButtonVariant.UNSTYLED}>
                <FontAwesomeIcon
                  style={{
                    marginRight: "4px",
                  }}
                  icon={faAngleLeft}
                />
                <Text>{backButtonText}</Text>
              </Button>
            )}
          </Box>
          {isLoadingReleaseTracks ? (
            <SoundWaveLoader height={100} width={100} />
          ) : (
            <ArtistReleaseTrackTable
              releaseId={releaseId}
              artistUserId={release?.artist_user_id}
              releaseTracks={releaseTracks || []}
              currency={currency}
              unitPrice={hidePurchaseButton ? undefined : unitPrice}
              selectedProductId={
                hidePurchaseButton ? undefined : productToGetPriceFrom?.id
              }
              lowestUnitPriceProductId={lowestPricedProduct?.id}
              releaseIsStreamEnabled={releaseIsStreamEnabled}
            />
          )}
        </Box>
        {/* TODO: Remove this and replace with actual credits structured data. */}
        <HardCodedReleaseCredits release={release} />
      </Box>
    </Box>
  );
};
